import React, { Suspense } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { toast } from "react-toastify";
import { allRoutes } from "src/constants/AllRoutes";
import { accountAtom } from "src/recoilState/account";
import { setItem } from "src/utils/storage";
import { getUser } from "src/services/UserService";
import { TOAST_IDS, localStorageKey } from "src/constants/generics";
import { itineraryAppliedOwnerFilter } from "src/recoilState/itinerary/itineraryFilter";
import { logoutUser } from "src/services/ApiHelpers";
import { Strings } from "src/resources";
import Loader from "src/components/Loader";

// normal routes
import AuthLayout from "src/layouts/AuthLayout";
import UserLayout from "src/layouts/UserLayout";
import ForgotPassword from "src/screens/Auth/ForgotPassword";
import Login from "src/screens/Auth/Login";
import ResetPassword from "src/screens/Auth/ResetPassword";
import permitRoutes from "./permitRoutesData";
import PermitRoutes from "./PermitRoutes";
import privateRoutes from "./privateRoutesData";
import ThemeSpinner from "src/components/ThemeSpinner";

export const Router = () => {
  const auth = getAuth();

  const userInfo = JSON.parse(localStorage.getItem(localStorageKey.userInfo));

  const setAccount = useSetRecoilState<AccountType | undefined>(accountAtom);
  const setAppliedOwnerRecoil = useSetRecoilState(itineraryAppliedOwnerFilter);
  const [currentUser, setCurrentUser] = useState<any>(userInfo?.uId);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        user
          .getIdTokenResult()
          .then((result) => {
            const authTokenData = JSON.parse(localStorage?.getItem(localStorageKey.authToken));
            if (!authTokenData?.authToken) {
              const authTokenObj = {
                lastLoginTime: result?.authTime,
                expirationTime: result?.expirationTime,
                authToken: result?.token
              };
              setItem(localStorageKey.authToken, JSON.stringify(authTokenObj));
            }
            getUserDetails(user?.email);
          })
          .catch(() => {
            setIsLoading(false);
          });
      } else {
        if (userInfo) {
          logoutUser();
          toast.success(Strings.logout_success_fully, { toastId: TOAST_IDS.logout });
        }
        setIsLoading(false);
      }
    });
  }, []);

  const getUserDetails = async (uid: string) => {
    if (uid) {
      const user = await getUser(uid);
      if (user?.success) {
        const data = user?.data;
        if (data?.permission) {
          const parsedPermissions = JSON.parse(data?.permission);
          if (Array.isArray(parsedPermissions)) {
            const object = JSON.parse(data?.permission).reduce((obj, item) => {
              const key = Object.keys(item)[0];
              return Object.assign(obj, { [key]: item[key] });
            }, {});
            data.permission = object;
          } else if (Object.keys(parsedPermissions)?.length) {
            data.permission = parsedPermissions;
          }
        }
        data.docId = user?.data?.emailId ? user.data.emailId : "";
        setItem(localStorageKey.userInfo, JSON.stringify(data));
        setAccount(data);
        setAppliedOwnerRecoil([{ id: data?.emailId || data?.id, userName: data?.name }]);
        setIsLoading(false);
      }
      setIsLoading(false);
    }
  };
  return (
    <BrowserRouter>
      {isLoading ? (
        <Loader />
      ) : (
        <Routes>
          {currentUser ? (
            <Route element={<UserLayout />}>
              <Route path="/*" element={<Navigate to={allRoutes.dashboard} replace />} />

              {privateRoutes.map(({ path, Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <Suspense fallback={<ThemeSpinner />}>
                      <Component />
                    </Suspense>
                  }
                />
              ))}
              {permitRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<PermitRoutes permission={route.permission} path={route.path} Component={route.component} />}
                />
              ))}
            </Route>
          ) : (
            //
            <Route element={<AuthLayout />}>
              <Route path="/" element={<Login />} />
              <Route path={allRoutes.login} element={<Login />} />
              <Route path={allRoutes.forgotPassword} element={<ForgotPassword />} />
              <Route path={allRoutes.resetPassword} element={<ResetPassword />} />
              <Route path={allRoutes.error} element={<Login />} />
            </Route>
          )}
        </Routes>
      )}
    </BrowserRouter>
  );
};
